import { useMemo, useState } from 'react';
import LayerGroup from './LayerGroup';
import { MapActiveLayer, WMSLayer, WMSMap } from '../../Map.types';
import LoadingSpinner from '../../../../components/loadingSpinner/LoadingSpinner';
import ExternalWmsLayers from './ExternalWmsLayers';
import IS_EFAS from '../../../../components/sitebase/Environment';
import FavouriteGroup from './FavouriteGroup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { useMinWidthQuery } from '../../../../hooks/useMediaQuery';
import CenteredModal from '../CenteredModal';
import Button from '@mui/material/Button';


import { WMSListPresentation } from './WMSList.types';

export default function WMSList(props: {
    baseWms: string,
    wmsMap: WMSMap,
    activeLayers: MapActiveLayer[],
    favourites: Set<string>,
    setFavouriteLayer: (layerId: string) => void,
    presentation: WMSListPresentation,
    anonymousUser: boolean,
    forecastEmbargoActive: boolean,
    forecastDate: string,
    addOrRemoveLayer: (source: string, group: string, layer: WMSLayer) => void
}) {
    const baseWms = Object.values(props.wmsMap).find((wms) => wms.url === props.baseWms);
    const extraWms = Object.entries(props.wmsMap).filter(([_, wmsinfo]) => wmsinfo.url !== props.baseWms);
    const SMALL_DISPLAY = useMinWidthQuery();

    const val = (grp: string) => {
        grp = grp.toLowerCase();
        if (grp.includes('flood summary')) {
            return IS_EFAS ? 1 : 2;
        } else if (grp.includes('hydrological')) {
            return IS_EFAS ? 2 : 3;
        } else if (grp.includes('init. conditions') || grp.includes('initial conditions')) {
            return IS_EFAS ? 3 : 1;
        } else if (grp.includes('flash flood') || grp.includes('flood risk')) {
            return 4;
        } else if (grp.includes('static')) {
            return IS_EFAS ? 5 : 6;
        } else if (grp.includes('evaluation')) {
            return IS_EFAS ? 6 : 5;
        } else if (grp.includes('meteorological')) {
            return IS_EFAS ? 7 : 2;
        } else if (grp.includes('gfm') || grp.includes('monitoring')) {
            return 8;
        } else {
            return 999;
        }
    };

    const sortedGroups = useMemo(() => {
        if (!baseWms) return [];
        return Object.entries(baseWms.layers).sort((a, b) => {
            return val(a[0]) - val(b[0]);
        })
        // .map(([group, layers]) => {
        //     const layerElements = [];
        //     for (let layer of layers) {
        //         // layers marker as "internal_layer" are not shown in the list of available layers
        //         // these layers should not be queried directly by the client, for example
        //         // glofas' reportingPoints is an alias for different internal layers depending on the request date
        //         if (layer.Keywords && layer.Keywords.internal_layer === "true") {
        //             // console.debug("Skipping addition of internal layer to layers list", layer.Name, layer);
        //             continue;
        //         }
        //         let toggled = props.activeLayers.find((l) => l.name === layer.Name && l.source === baseWms?.url) ? true : false;
        //         layerElements.push(
        //             <li key={layer.Name + toggled} data-layer-name={layer.Name} data-layer-source={baseWms?.name} className='dropdown-item' onClick={() => {
        //                 props.addLayer(baseWms!.url, group, layer);
        //             }}>
        //                 <LayerToggle className='d-inline-block me-2' toggleLayer={() => props.addLayer(baseWms!.url, group, layer)} toggled={toggled} id={''} />
        //                 {layer.Title}</li>);
        //     }
        //     return <LayerGroup key={group} group={group} layerElements={layerElements} />
        // })
    }, [baseWms]);

    const favouritesGroup = useMemo(() => {
        return sortedGroups
            .map(([group, layers]) => ({
                group,
                layers: layers.filter(layer => props.favourites.has(layer.Title)).sort((a, b) => {
                    const a_num = parseInt(a.Title);
                    const b_num = parseInt(b.Title);
                    if (!isNaN(a_num) && !isNaN(b_num)) {
                        return a_num - b_num || a.Title.localeCompare(b.Title);
                    } else if (!isNaN(a_num)) {
                        return -1;
                    } else if (!isNaN(b_num)) {
                        return 1;
                    } else {
                        return a.Title.localeCompare(b.Title);
                    }
                })
            }))
    }, [sortedGroups, props.favourites]);

    const [layersModalOpen, setLayersModalOpen] = useState(false);
    const presentation = !SMALL_DISPLAY ? props.presentation : WMSListPresentation.TEXT;
    let elements = sortedGroups && baseWms && <><span key={baseWms?.name}>
        <ul className={`dropdown list-group ${SMALL_DISPLAY ? "list-group-vertical" : "list-group-horizontal-sm"} wms-layer-group`}>
            {props.favourites.size > 0 && <FavouriteGroup
                key="favourites"
                baseWms={baseWms}
                layers={favouritesGroup}
                favourites={props.favourites}
                setFavouriteLayer={props.setFavouriteLayer}
                addLayer={props.addOrRemoveLayer}
                activeLayers={props.activeLayers}
                presentation={presentation}
                anonymousUser={props.anonymousUser}
                forecastDate={props.forecastDate}
                forecastEmbargoActive={props.forecastEmbargoActive} />}

            {sortedGroups.map(([group, layers]) => {
                return <LayerGroup
                    key={props.baseWms + group}
                    baseWms={baseWms}
                    group={group}
                    layers={layers}
                    favourites={props.favourites}
                    setFavouriteLayer={props.setFavouriteLayer}
                    addLayer={props.addOrRemoveLayer}
                    activeLayers={props.activeLayers}
                    presentation={presentation}
                    anonymousUser={props.anonymousUser}
                    forecastDate={props.forecastDate}
                    forecastEmbargoActive={props.forecastEmbargoActive} />
            })}
        </ul>
    </span>
        {extraWms?.length > 0 && <div key={extraWms?.length} className='my-auto align-middle'>
            {extraWms && <ExternalWmsLayers
                extraWms={extraWms}
                activeLayers={props.activeLayers}
                addLayer={props.addOrRemoveLayer}
                anonymousUser={props.anonymousUser}
                forecastEmbargoActive={props.forecastEmbargoActive}
                forecastDate={props.forecastDate}

            />}
        </div>}</>

    if (SMALL_DISPLAY) {
        elements = <>
            <Button variant='text' className='ms-1 text-dark' onClick={() => setLayersModalOpen(true)}>
                Layers <FontAwesomeIcon icon={faLayerGroup} className='ms-1' />
            </Button>
            <CenteredModal
                open={layersModalOpen}
                onClose={() => setLayersModalOpen(false)}
                title='Layers'
                content={elements}
                actions={<Button onClick={() => setLayersModalOpen(false)}>Close</Button>}
                width='95vw'
                maxContentHeight='70vh'
            />
        </>;
    }

    return <div>
        {sortedGroups && baseWms &&
            <div className='d-flex'>
                {elements}
            </div>
            ||
            <div className='d-flex flex-row justify-content-center'
                style={{ maxHeight: "8vh" }}>
                <div className='my-auto mx-3'>Loading WMS...</div><LoadingSpinner className='text-center p-0' maxHeight='5vh' />
            </div>
        }
    </div>
};